/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import { EventGroup, StatusCode, WebsocketTypes } from '@/api/enumerations';
import {
  cancelDelegatedSession,
  getDelegatedSessionsByClient,
  getOversightClientsById,
  getOversightClientsCompany,
  getSessionsByClient,
  startDelegatedSession,
} from '@/api/oversight';
import {
  ClientProps,
  DelegatedAccessStatusUpdatePayload,
  DelegatedSessions,
  OversightClientCompanyProps,
  Sessions,
} from '@/api/oversight/types';
import { DelegatedAccessDialog } from '@/components/Dialog/DelegatedAccess';
import { Search } from '@/components/Search';
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import Snackbar from '@/components/Snackbar';
import {
  IconAdminPanelSettingsMS,
  IconArrowCircleLeftMS,
  IconArrowForwardIosMS,
  IconCheckMS,
  IconOpenInNewMS,
  IconPageviewMS,
} from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import { WebsocketContext } from '@/context/websocketMessage';
import {
  formatCnpjCpf,
  formatDateAndTime,
  formatPhoneNumber,
  getCompanySubdomain,
} from '@/helpers';
import {
  getLocalStorageDelegatedSessionId,
  getLocalStorageImpersonationToken,
  getLocalStorageOversightToken,
  removeLocalStorageImpersonation,
  setLocalStorageImpersonation,
} from '@/helpers/localStorage';
import { useDebounce } from '@/hooks/useDebounce';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { BackButton } from '@/pages/Hive/Tabs/CompanyInfo/styles';
import { Button } from '@/pages/Oversight/components/Button';
import { Card, CardData } from '@/pages/Oversight/components/Card';
import { CountBadge } from '@/pages/Oversight/components/Card/styles';
import { ReasonAccordion } from '@/pages/Oversight/components/ReasonAccordion';
import { Table, TableHeadData } from '@/pages/Oversight/components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '@/pages/Oversight/components/Table/styles';
import { TabItem } from '@/pages/Oversight/components/TabsPanel/types';
import { GridMainContainer, RoundedGreen } from '@/pages/Oversight/styles';
import {
  delegatedAccessLabels,
  expiredDetail,
  formatDate,
  getTerminatedByLabel,
  normalizeStr,
} from '@/pages/Oversight/utils';
import {
  DragIndicator,
  Search as SearchIcon,
  WarningOutlined,
} from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import jwtDecode from 'jwt-decode';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { useNavigate, useParams } from 'react-router-dom';

import { SectionClient } from '../SectionClient';
import { AmountOFClients, TableDataName } from '../styles';
import {
  CardContainer,
  CompanyMenuItem,
  CompanySelect,
  ContainerGroup,
  DelegatedSessionsContainer,
  Panel,
  Section,
  StyledDialog,
  StyledSeparator,
  StyledTitle,
} from './styles';

const ongoingDelegatedStatuses = ['pending_consent', 'approved', 'active'];

// Only a session that reached `active` has an impersonation token stored, and
// from there it can only end as `ended` or `revoked`.
const startedDelegatedEndStatuses = ['ended', 'revoked'];

const delegatedAccessRow = [
  { id: 1, label: 'Criado em', key: 'created_at' },
  { id: 2, label: 'Supervisor', key: 'supervisor_name' },
  { id: 3, label: 'Status', key: 'status' },
  { id: 4, label: 'Motivo', key: 'reason' },
  { id: 5, label: 'Ação', key: 'action', sortable: false },
] satisfies TableHeadData<DelegatedSessions>[];

const sectionClientRow = [
  { id: 1, label: 'Início da sessão', key: 'started_at' },
  { id: 2, label: 'Status', key: 'is_active' },
  { id: 3, label: 'Erros na sessão', key: 'client_error_count' },
  { id: 4, label: 'Ação', key: 'action', sortable: false },
] satisfies TableHeadData<Sessions>[];

const subscriptionTypeLabels: Record<string, string> = {
  monthly: 'Mensal',
  annual: 'Anual',
  yearly: 'Anual',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
};

function subscriptionLabel(type: string | null, installments: boolean): string {
  if (!type) return '-';
  const label = subscriptionTypeLabels[type] ?? type;
  return installments ? `${label} (parcelada)` : label;
}

function buildCompanyCardData(
  company: OversightClientCompanyProps,
  totalCompanies: number,
  companySelector: ReactNode
): CardData[] {
  return [
    {
      label: 'Empresa',
      labelBadge: totalCompanies > 1 && (
        <CountBadge>{totalCompanies} empresas</CountBadge>
      ),
      value: company.name ?? '-',
      valueElement: companySelector,
    },
    { label: 'CNPJ', value: company.cnpj ? formatCnpjCpf(company.cnpj) : '-' },
    {
      label: 'Laudos e amostras disponíveis',
      value: `${company.available_reports ?? 0} laudos • ${
        company.available_samples ?? 0
      } amostras`,
    },
    { label: 'Plano contratado', value: company.subscription_plan ?? '-' },
    {
      label: 'Assinatura',
      value: subscriptionLabel(company.subscription_type, company.installments),
    },
    {
      label: 'Próximo pagamento',
      value: company.next_payment
        ? formatDateAndTime(company.next_payment)
        : '-',
    },
  ];
}

export function CustomerDetails(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [openDialogDelegatedAccess, setOpenDialogDelegatedAccess] =
    useState(false);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [, setSelectedTab] = useState<string>('');
  const [infoClient, setInfoClient] = useState<CardData[]>([]);
  const [client, setClient] = useState<ClientProps>();
  const [delegatedSessions, setDelegatedSessions] = useState<
    DelegatedSessions[]
  >([]);
  const [searchDelegatedSessions, setSearchDelegatedSessions] = useState('');
  const [searchSessions, setSearchSessions] = useState('');
  const [sessions, setSessions] = useState<Sessions[]>([]);
  const [sessionsRecords, setSessionsRecords] = useState(0);
  const [delegatedRecords, setDelegatedRecords] = useState(0);
  // Tracks emptiness of the unfiltered list, so the search field is not
  // disabled just because the current search returned nothing.
  const [hasAnySessions, setHasAnySessions] = useState(false);
  const [hasAnyDelegatedSessions, setHasAnyDelegatedSessions] = useState(false);
  const [page, setPage] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);
  const [activitySessionId, setActivitySessionId] = useState<number>();
  const [activitySource, setActivitySource] =
    useState<TabItem['source']>('section_client');
  const [companies, setCompanies] = useState<OversightClientCompanyProps[]>([]);
  const [companiesRecords, setCompaniesRecords] = useState(0);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0);
  const [sectionErrorCount, setSectionErrorCount] = useState(0);
  const [device, setDevice] = useState('');
  const [endedAt, setEndedAt] = useState<string | null>('');
  const [startedAt, setStartAt] = useState<string | null>('');
  const [delegatedSessionId, setDelegatedSessionId] = useState<number | null>(
    null
  );

  const { id } = useParams();
  const { handleSnackbar } = useSnackbar();
  const { openSnackbar } = useContext(GlobalContext);
  const { websocketMessage } = useContext(WebsocketContext);
  const { getErrorMessage } = useErrorMessage();
  const debounceSessions = useDebounce(searchSessions);
  const debounceDelegatedSessions = useDebounce(searchDelegatedSessions);

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'oversight-customer-details',
    panelIds: showDrawer ? ['left', 'right'] : ['left'],
  });
  const { oversightUserRole } = useTokenRefresh();

  const navigate = useNavigate();

  const oversightToken = getLocalStorageOversightToken();
  const loggedSupervisorId = oversightToken
    ? Number(jwtDecode<{ sub: string }>(oversightToken).sub)
    : null;

  const hasBlockingDelegatedAccess = delegatedSessions.some((session) =>
    ongoingDelegatedStatuses.includes(session.status)
  );

  const blockingAccessTooltip =
    'Já existe uma solicitação de acesso delegado aguardando aprovação do cliente.';

  const openDelegatedAccessTooltip = 'Ir até o acesso delegado';

  const handleOpenDialog = (): void => {
    setOpenDialogDelegatedAccess(!openDialogDelegatedAccess);
  };

  const handleOpenActivity = (row: Sessions): void => {
    setShowDrawer(true);
    setActivitySource('section_client');
    setActivitySessionId(row.id);
    setSectionErrorCount(row.client_error_count);
    setDevice(row.device);
    setEndedAt(row.ended_at);
    setStartAt(row.started_at);
    setDelegatedSessionId(row.delegated_session_id);

    const alreadyOpen = tabs.find((tab) => tab.id === row.id);
    if (alreadyOpen) {
      setSelectedTab(alreadyOpen.value);
      return;
    }

    const newTab: TabItem = {
      id: row.id,
      value: crypto.randomUUID(),
      title: 'Clientes',
      subtitle: row.started_at,
      source: 'section_client',
    };
    setTabs((prev) => [...prev, newTab]);
  };

  const handleOpenDelegatedActivity = (row: DelegatedSessions): void => {
    setShowDrawer(true);
    setActivitySource('delegated_access');
    setActivitySessionId(undefined);
    setSectionErrorCount(0);
    setDevice('');
    setStartAt(row.session_started_at);
    setEndedAt(row.last_activity_at);
    setDelegatedSessionId(row.id);
  };

  const handleClose = (): void => {
    setShowDrawer(false);
  };

  const getClientsById = useCallback(async () => {
    try {
      const { data, detail } = await getOversightClientsById(Number(id));

      if (detail?.description) throw new Error(detail.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente');
      }

      if (data) {
        setClient(data);
        setInfoClient([
          { label: 'Nome', value: data.name },
          {
            label: 'Status',
            value: data.is_active,
            user_status: data.is_active ? 'active' : 'inactive',
          },
          { label: 'ID', value: data.id },
          { label: 'CPF', value: formatCnpjCpf(data.cpf) },
          { label: 'E-mail', value: data.email },
          { label: 'Telefone', value: formatPhoneNumber(data.phone) },
        ]);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [handleSnackbar, getErrorMessage, id]);

  const getClientsCompany = useCallback(async () => {
    try {
      const { data, detail } = await getOversightClientsCompany(Number(id));

      if (detail?.description) throw new Error(detail.description);

      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente');
      }

      if (!data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setCompanies(data.companies);
      setCompaniesRecords(data.records);
      setSelectedCompanyIndex(0);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [handleSnackbar, getErrorMessage, id]);

  const infoCompany = useMemo<CardData[]>(() => {
    const selectedCompany = companies[selectedCompanyIndex];

    if (!selectedCompany) return [];

    const companySelector = companiesRecords > 1 && (
      <CompanySelect
        select
        size="small"
        value={selectedCompanyIndex}
        onChange={(e) => setSelectedCompanyIndex(Number(e.target.value))}
        SelectProps={{
          renderValue: () => selectedCompany.name ?? '-',
        }}
      >
        {companies.map((company, index) => (
          <CompanyMenuItem key={company.cnpj ?? index} value={index}>
            {company.name ?? `Empresa ${index + 1}`}
            {index === selectedCompanyIndex && IconCheckMS}
          </CompanyMenuItem>
        ))}
      </CompanySelect>
    );

    return buildCompanyCardData(
      selectedCompany,
      companiesRecords,
      companySelector
    );
  }, [companies, companiesRecords, selectedCompanyIndex]);

  const getDelegatedSessions = useCallback(
    async (params: string, group?: EventGroup) => {
      try {
        const { data, detail } = await getDelegatedSessionsByClient(
          Number(id),
          params,
          group,
          page
        );

        if (detail?.description) throw new Error(detail.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente');
        }

        if (data) {
          setDelegatedSessions(data.delegated_sessions);
          setDelegatedRecords(data.records);

          if (!params) setHasAnyDelegatedSessions(data.records > 0);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [id, handleSnackbar, getErrorMessage, page]
  );

  const getSessions = useCallback(
    async (search: string) => {
      try {
        const { data, detail } = await getSessionsByClient(
          Number(id),
          search,
          sessionsPage
        );

        if (detail?.description) throw new Error(detail.description);

        if (detail?.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente');
        }

        if (data.sessions) {
          setSessions(data.sessions);
          setSessionsRecords(data.records);

          if (!search) setHasAnySessions(data.records > 0);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [id, handleSnackbar, getErrorMessage, sessionsPage]
  );

  const refreshDelegatedSessions = (): void => {
    getDelegatedSessions(normalizeStr(debounceDelegatedSessions));
  };

  const refreshSessions = useCallback((): void => {
    getSessions(normalizeStr(debounceSessions));
  }, [getSessions, debounceSessions]);

  const handleCancelDelegatedSession = async (
    sessionId: number
  ): Promise<void> => {
    try {
      const response = await cancelDelegatedSession(sessionId);

      if (response.detail?.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      handleSnackbar('Solicitação cancelada.', false);
      refreshDelegatedSessions();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (cancelTargetId === null) {
      return;
    }
    await handleCancelDelegatedSession(cancelTargetId);
    setCancelTargetId(null);
  };

  const handleOpenDelegatedAccess = (impersonationToken: string): void => {
    const protoBaseUrl = getCompanySubdomain().includes('-st')
      ? 'https://app-st.protoai.com.br'
      : 'https://app.protoai.com.br';

    window.open(
      `${protoBaseUrl}/home?impersonation_token=${impersonationToken}`,
      '_blank'
    );
  };

  const handleStartDelegatedSession = async (
    sessionId: number
  ): Promise<void> => {
    try {
      const response = await startDelegatedSession(sessionId);

      if (response.detail?.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      const impersonationToken = response.data?.impersonation_token;

      if (impersonationToken) {
        setLocalStorageImpersonation(impersonationToken, sessionId);
        handleOpenDelegatedAccess(impersonationToken);
      }

      handleSnackbar('Acesso delegado iniciado!', false);
      refreshDelegatedSessions();
      setTimeout(() => {
        refreshSessions();
      }, 5000);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const renderDelegatedStatus = (row: DelegatedSessions): ReactNode => {
    // console.log('row', row);
    const label = delegatedAccessLabels[row.status] ?? row.status;
    const detail = expiredDetail(row);

    if (!detail) {
      return label;
    }

    return (
      <>
        {label}
        <Tooltip title={detail.tooltip} placement="top">
          <SubtitleCell>{detail.label}</SubtitleCell>
        </Tooltip>
      </>
    );
  };

  const renderSessionStatus = (row: Sessions): ReactNode => {
    if (!row.ended_at) {
      return (
        <div>
          <RoundedGreen />
          Online
        </div>
      );
    }

    return (
      <>
        {getTerminatedByLabel(row.terminated_by)}
        <SubtitleCell>
          <span>{formatDate(row.ended_at, 'absolute')}</span>
        </SubtitleCell>
      </>
    );
  };

  const renderDelegatedActivityButton = (row: DelegatedSessions): ReactNode => (
    <Button
      variant="outlined"
      style={{ width: '100%' }}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenDelegatedActivity(row);
      }}
      disabled={row.activity_count === 0}
    >
      Ver atividade ({row.activity_count > 999 ? '999+' : row.activity_count})
      {IconPageviewMS}
    </Button>
  );

  const renderDelegatedAction = (row: DelegatedSessions): ReactNode => {
    const isOwner = loggedSupervisorId === row.supervisor_id;

    if (oversightUserRole !== 'supervisor' || !isOwner) {
      return renderDelegatedActivityButton(row);
    }

    if (row.status === 'pending_consent') {
      return (
        <Button
          variant="outlined"
          isRedButton
          style={{ width: '100%' }}
          onClick={() => setCancelTargetId(row.id)}
        >
          Cancelar solicitação
        </Button>
      );
    }

    if (row.status === 'approved') {
      return (
        <Button
          variant="contained"
          style={{ width: '100%' }}
          onClick={() => handleStartDelegatedSession(row.id)}
        >
          Iniciar acesso delegado
        </Button>
      );
    }

    if (row.status === 'active') {
      const impersonationToken = getLocalStorageImpersonationToken();
      const isStoredSession = getLocalStorageDelegatedSessionId() === row.id;

      if (!impersonationToken || !isStoredSession) {
        return renderDelegatedActivityButton(row);
      }

      return (
        <Tooltip title={openDelegatedAccessTooltip} placement="top">
          <span style={{ display: 'block', width: '100%' }}>
            <Button
              variant="contained"
              style={{ width: '100%' }}
              onClick={() => handleOpenDelegatedAccess(impersonationToken)}
            >
              {IconOpenInNewMS}
            </Button>
          </span>
        </Tooltip>
      );
    }

    return renderDelegatedActivityButton(row);
  };

  useEffect(() => {
    const loadClientData = async (): Promise<void> => {
      await Promise.all([getClientsById(), getClientsCompany()]);
      setIsLoading(false);
    };

    loadClientData();
  }, []);

  useEffect(() => {
    getDelegatedSessions(normalizeStr(debounceDelegatedSessions));

    // TODO: trocar polling por websocket dedicado assim que o backend
    // corrigir a instabilidade do servidor de desenvolvimento.
    const pollingId = setInterval(() => {
      getDelegatedSessions(
        normalizeStr(debounceDelegatedSessions),
        EventGroup.BACKGROUND
      );
    }, 10000);

    return () => clearInterval(pollingId);
  }, [debounceDelegatedSessions, page]);

  useEffect(() => {
    getSessions(normalizeStr(debounceSessions));
  }, [debounceSessions, sessionsPage]);

  const applyDelegatedAccessStatusUpdate = useCallback(
    (message: MessageEvent<string>) => {
      const messageData = JSON.parse(message.data);

      if (messageData.type !== WebsocketTypes.DELEGATED_ACCESS_STATUS_UPDATE) {
        return;
      }

      const payload = messageData.payload as DelegatedAccessStatusUpdatePayload;

      setDelegatedSessions((prev) =>
        prev.map((session) =>
          session.id === payload.delegated_session_id
            ? {
                ...session,
                status: payload.status,
                reason: payload.reason,
                consent_expires_at: payload.consent_expires_at,
                approval_expires_at: payload.approval_expires_at,
                session_started_at: payload.session_started_at,
              }
            : session
        )
      );

      const hasEnded = startedDelegatedEndStatuses.includes(payload.status);
      const isStoredSession =
        getLocalStorageDelegatedSessionId() === payload.delegated_session_id;

      if (hasEnded && isStoredSession) {
        removeLocalStorageImpersonation();
      }
    },
    []
  );

  useEffect(() => {
    if (!websocketMessage) return;
    applyDelegatedAccessStatusUpdate(websocketMessage);
  }, [websocketMessage]);

  return (
    <>
      <ContainerGroup
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel id="left" minSize="50%">
          <GridMainContainer>
            <BackButton onClick={() => navigate(-1)}>
              {IconArrowCircleLeftMS}
            </BackButton>
            <StyledTitle>
              {IconAdminPanelSettingsMS} PROTO OVERSIGHT {IconArrowForwardIosMS}{' '}
              Clientes {IconArrowForwardIosMS} {client?.name}
            </StyledTitle>
            <CardContainer>
              <Card
                title="Informações do cliente"
                cardData={infoClient}
                isLoading={isLoading}
              />
              <Card
                title="Informações da empresa"
                cardData={infoCompany}
                isLoading={isLoading}
              />
            </CardContainer>

            <Section>
              <div>
                <TableDataName>Acessos Delegados</TableDataName>
                {isLoading ? (
                  <SkeletonWrapper
                    isLoading={isLoading}
                    variant="text"
                    height={28}
                    width={128}
                  />
                ) : (
                  <AmountOFClients>
                    {delegatedRecords}{' '}
                    {delegatedRecords === 1 ? 'registro' : 'registros'}
                  </AmountOFClients>
                )}
              </div>

              <DelegatedSessionsContainer>
                {oversightUserRole === 'supervisor' && (
                  <Tooltip
                    title={
                      hasBlockingDelegatedAccess ? blockingAccessTooltip : ''
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleOpenDialog}
                        disabled={hasBlockingDelegatedAccess}
                      >
                        Solicitar acesso delegado {IconAdminPanelSettingsMS}
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Search
                  withoutBorder
                  width="375px"
                  placeholder="Buscar por supervisor ou motivo..."
                  value={searchDelegatedSessions}
                  setSearchField={setSearchDelegatedSessions}
                  disabled={!hasAnyDelegatedSessions}
                  maxLength={50}
                />
              </DelegatedSessionsContainer>
            </Section>

            <Table
              data={delegatedSessions}
              tableHeadData={delegatedAccessRow}
              getRowId={(row) => row.id}
              emptyTableIcon={<SearchIcon />}
              emptyTableDescription="Nenhum resultado encontrado"
              records={delegatedRecords}
              page={page}
              setPage={setPage}
              fixedLayout
              renderRow={(row) => (
                <>
                  <StyledTableCellBody>
                    {formatDate(row.created_at, 'absolute')}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {row.supervisor_name}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderDelegatedStatus(row)}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    <ReasonAccordion reason={row.reason} />
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderDelegatedAction(row)}
                  </StyledTableCellBody>
                </>
              )}
            />

            <Section>
              <div className="values">
                <TableDataName>Sessões do cliente</TableDataName>

                {isLoading ? (
                  <SkeletonWrapper
                    isLoading={isLoading}
                    variant="text"
                    height={28}
                    width={128}
                  />
                ) : (
                  <AmountOFClients>
                    {sessionsRecords}{' '}
                    {sessionsRecords === 1 ? 'sessão' : 'sessões'}
                  </AmountOFClients>
                )}
              </div>

              <Search
                value={searchSessions}
                setSearchField={setSearchSessions}
                placeholder="Busca por status ou erro..."
                width="375px"
                withoutBorder
                disabled={!hasAnySessions}
                maxLength={50}
              />
            </Section>

            <Table
              data={sessions}
              tableHeadData={sectionClientRow}
              getRowId={(row) => row.id}
              emptyTableIcon={<SearchIcon />}
              emptyTableDescription="Nenhum resultado encontrado"
              records={sessionsRecords}
              page={sessionsPage}
              setPage={setSessionsPage}
              renderRow={(row) => (
                <>
                  <StyledTableCellBody>
                    {formatDate(row.started_at, 'absolute')}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderSessionStatus(row)}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {row.client_error_count === 0 ? (
                      '-'
                    ) : (
                      <div>
                        <WarningOutlined />
                        {row.client_error_count}
                      </div>
                    )}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    <Button
                      variant="outlined"
                      style={{ width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenActivity(row);
                      }}
                    >
                      Ver atividade (
                      {row.session_event_count > 999
                        ? '999+'
                        : row.session_event_count}
                      ){IconPageviewMS}
                    </Button>
                  </StyledTableCellBody>
                </>
              )}
            />
          </GridMainContainer>
        </Panel>
        <StyledSeparator className="resize-handle">
          <DragIndicator fontSize="small" />
        </StyledSeparator>
        {showDrawer && (
          <Panel id="right">
            {' '}
            <SectionClient
              sessionId={activitySessionId}
              source={activitySource}
              onClose={() => handleClose()}
              onSessionEnded={refreshSessions}
              userRole={oversightUserRole}
              error_count={sectionErrorCount}
              device={device}
              started_at={startedAt}
              ended_at={endedAt}
              delegated_session_id={delegatedSessionId}
            />
          </Panel>
        )}
      </ContainerGroup>
      <DelegatedAccessDialog
        open={openDialogDelegatedAccess}
        handleClose={handleOpenDialog}
        title="Solicitar acesso delegado"
        clientId={client?.id ?? Number(id)}
        clientName={client?.name ?? ''}
        onSuccess={() =>
          getDelegatedSessions(normalizeStr(debounceDelegatedSessions))
        }
      />
      <StyledDialog
        open={cancelTargetId !== null}
        onClose={() => setCancelTargetId(null)}
      >
        <Box
          p="40px"
          width="640px"
          display="flex"
          flexDirection="column"
          gap="20px"
          position="relative"
          boxSizing="border-box"
          overflow="hidden"
        >
          <Box>
            <Typography variant="h6" sx={{ marginBottom: '8px' }}>
              Cancelar solicitação?
            </Typography>
            <Typography sx={{ marginBottom: '24px', color: 'common.grey' }}>
              O cliente não receberá mais notificações sobre este acesso. Você
              poderá enviar uma nova solicitação quando necessário.
            </Typography>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              <Button
                variant="outlined"
                rounded
                style={{ flex: 1 }}
                onClick={() => setCancelTargetId(null)}
              >
                Voltar
              </Button>
              <Button
                variant="outlined"
                rounded
                isRedButton
                style={{ flex: 1 }}
                onClick={handleConfirmCancel}
              >
                Cancelar solicitação
              </Button>
            </Box>
          </Box>
        </Box>
      </StyledDialog>
      {openSnackbar && <Snackbar />}
    </>
  );
}
